import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} BulkQuantityItem
 * @property {string} variantId - The variant the row resolves to.
 * @property {number} quantity - The quantity selected for that variant.
 */

/**
 * A list of product option values, each with its own quantity stepper, that are added to
 * the cart together in a single request.
 *
 * Lives inside `<variant-picker>` so that picking another option (a colour, say) re-renders
 * the rows server side with the prices and availability of the newly selected combination.
 *
 * @extends Component
 */
class BulkQuantityListComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    // `<variant-picker>` treats any bubbling `change` as an option change and refetches the
    // section. The stepper inputs are not option controls, so their events stop here.
    this.addEventListener('change', this.#stopOptionEvents);
    this.addEventListener('input', this.#stopOptionEvents);

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);

    this.#publish();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.#stopOptionEvents);
    this.removeEventListener('input', this.#stopOptionEvents);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // The rows are morphed away on an option change; clear the totals the product form is
    // holding so a stale selection can never be submitted.
    this.#dispatch([]);
  }

  /** @returns {HTMLInputElement[]} */
  get inputs() {
    return /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('.bulk-quantity-list__input')));
  }

  /**
   * The rows that currently carry a quantity.
   * @returns {BulkQuantityItem[]}
   */
  get selectedItems() {
    /** @type {BulkQuantityItem[]} */
    const items = [];

    for (const input of this.inputs) {
      const quantity = Number(input.value);
      const variantId = input.dataset.variantId;

      if (!variantId || !Number.isFinite(quantity) || quantity <= 0) continue;

      items.push({ variantId, quantity });
    }

    return items;
  }

  /** @returns {number} */
  get totalQuantity() {
    return this.selectedItems.reduce((total, item) => total + item.quantity, 0);
  }

  /** Resets every row back to zero. */
  reset() {
    for (const input of this.inputs) {
      input.value = '0';
    }

    this.#publish();
  }

  /** @param {Event} event */
  #stopOptionEvents = (event) => {
    event.stopPropagation();

    if (event.type === 'input' || event.type === 'change') {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.classList.contains('bulk-quantity-list__input')) {
        this.#normalize(target);
        this.#publish();
      }
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('button[data-step]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();

    const input = this.#inputFor(button);
    if (!input) return;

    const direction = button.dataset.step === 'up' ? 1 : -1;
    const step = Number(input.step) || 1;
    const minQuantity = Number(input.dataset.minQuantity) || step;
    const current = Number(input.value) || 0;

    // Stepping up from nothing jumps straight to the variant's minimum order quantity,
    // which is the increment unless the merchant set a higher one.
    let next = current === 0 && direction === 1 ? minQuantity : current + step * direction;

    // Stepping back below the minimum empties the row rather than stranding it at a
    // quantity that could not be ordered.
    if (next < minQuantity) next = 0;

    input.value = String(next);
    this.#normalize(input);
    this.#publish();
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    // The rows sit inside the variant picker's own form. Enter would submit that form and
    // reload the page, so commit the value instead.
    if (event.key !== 'Enter') return;

    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.classList.contains('bulk-quantity-list__input')) return;

    event.preventDefault();
    this.#normalize(target);
    this.#publish();
  };

  /**
   * @param {HTMLButtonElement} button
   * @returns {HTMLInputElement | null}
   */
  #inputFor(button) {
    const input = button.parentElement?.querySelector('.bulk-quantity-list__input');
    return input instanceof HTMLInputElement ? input : null;
  }

  /**
   * Clamps a typed or stepped value to the row's min, max and increment.
   * @param {HTMLInputElement} input
   */
  #normalize(input) {
    const step = Number(input.step) || 1;
    const minQuantity = Number(input.dataset.minQuantity) || step;
    const max = input.max === '' ? Infinity : Number(input.max);

    let value = Math.floor(Number(input.value));

    if (!Number.isFinite(value) || value <= 0) {
      input.value = '0';
      return;
    }

    if (value < minQuantity) {
      value = minQuantity;
    } else if (step > 1) {
      // Round up to the next valid increment above the minimum.
      const stepsAboveMin = Math.ceil((value - minQuantity) / step);
      value = minQuantity + stepsAboveMin * step;
    }

    if (value > max) {
      // Fall back to the largest valid increment that still fits under the maximum.
      const stepsAboveMin = Math.floor((max - minQuantity) / step);
      value = stepsAboveMin < 0 ? 0 : minQuantity + stepsAboveMin * step;
    }

    input.value = String(value);
  }

  #publish() {
    this.#dispatch(this.selectedItems);
  }

  /** @param {BulkQuantityItem[]} items */
  #dispatch(items) {
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.bulkQuantityUpdate, {
        detail: {
          sectionId: this.dataset.sectionId ?? '',
          productId: this.dataset.productId ?? '',
          items,
          totalQuantity: items.reduce((total, item) => total + item.quantity, 0),
        },
      })
    );
  }
}

if (!customElements.get('bulk-quantity-list')) {
  customElements.define('bulk-quantity-list', BulkQuantityListComponent);
}
